import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import { Button, TextField, Paper, Typography, Box, Accordion, AccordionSummary, AccordionDetails, AppBar, Tabs, Tab, Grid } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useHistory, useParams } from "react-router-dom";
import { getLiteralById, updateLiteral } from "../../../../api/literal";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useSelector, shallowEqual } from "react-redux";

function getEmptyLiteral() {
  return {
    nombre: "",
    content: {},
  };
}

// Componente para renderizar un campo de literal (puede ser string o objeto anidado)
function LiteralField({ path, value, onChange, level = 0, selectedLanguage }) {
  const isMultilang = value && typeof value === "object" && !Array.isArray(value) && (value.es !== undefined || value.en !== undefined || value.ca !== undefined);
  const isNested = value && typeof value === "object" && !Array.isArray(value) && !isMultilang;

  if (isMultilang) {
    // Es un campo multilenguaje (tiene es, en, ca)
    // Mostrar solo el input del idioma seleccionado
    return (
      <Box mb={2} pl={level * 2}>
        <Typography variant="subtitle2" style={{ marginBottom: "8px", fontWeight: "bold" }}>
          {path.split('.').pop()}
        </Typography>
        <TextField
          fullWidth
          label={`${path.split('.').pop()}`}
          value={value[selectedLanguage] || ""}
          onChange={(e) => {
            const newValue = { ...value, [selectedLanguage]: e.target.value };
            onChange(path, newValue);
          }}
          variant="outlined"
          margin="normal"
          multiline
          rows={2}
        />
      </Box>
    );
  } else if (isNested) {
    // Es un objeto anidado, renderizar recursivamente
    // Dentro de subsecciones, los campos se muestran en dos columnas
    return (
      <Accordion defaultExpanded={level < 2} style={{ marginBottom: "8px" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            {path.split('.').pop()}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <Grid container spacing={2}>
              {Object.keys(value).map((key) => {
                const nestedValue = value[key];
                // Si es un objeto anidado (subsección), ocupa el ancho completo
                const isNestedSubsection = nestedValue && typeof nestedValue === "object" && !Array.isArray(nestedValue) && 
                  !(nestedValue.es !== undefined || nestedValue.en !== undefined || nestedValue.ca !== undefined);
                
                return (
                  <Grid item xs={12} sm={isNestedSubsection ? 12 : 6} key={key}>
                    <LiteralField
                      path={`${path}.${key}`}
                      value={nestedValue}
                      onChange={onChange}
                      level={level + 1}
                      selectedLanguage={selectedLanguage}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  return null;
}

export default function EditLiteralsPage() {
  const [literal, setLiteral] = useState(getEmptyLiteral());
  const [selectedLanguage, setSelectedLanguage] = useState("es");
  const literalId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading,
    ContentSkeleton,
  } = useSkeleton();

  const languages = useSelector((store) => store.authentication?.languages, shallowEqual);

  useEffect(() => {
    if (!literalId) {
      disableLoading();
      alertError({ error: null, customMessage: "Could not get literal." });
      return history.push("/literals");
    }
    getLiteralById(literalId)
      .then((res) => {
        if (res.status === 200) {
          setLiteral(res.data);
          disableLoading();
        }
      })
      .catch((error) => {
        alertError({ error: error, customMessage: "Could not get literal." });
        disableLoading();
        history.push("/literals");
      });
  }, [literalId, disableLoading, history]);

  function saveLiteral() {
    updateLiteral(literalId, literal)
      .then((res) => {
        if (res.status === 200) {
          alertSuccess({
            title: "Saved!",
            customMessage: "Changes successfully saved.",
          });
          history.push("/literals");
        }
      })
      .catch((error) => {
        alertError({ error: error, customMessage: "Could not save changes." });
      });
  }

  const handleFieldChange = (path, newValue) => {
    const keys = path.split(".");
    const newContent = { ...literal.content };

    // Navegar hasta el penúltimo nivel
    let current = newContent;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }

    // Asignar el nuevo valor
    current[keys[keys.length - 1]] = newValue;

    setLiteral({ ...literal, content: newContent });
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Filtrar idiomas válidos
  const validLanguages = languages
    ? languages.filter(
        (lang) =>
          lang &&
          lang.fullName &&
          lang.fullName.trim() !== "" &&
          lang.fullName.toLowerCase() !== "unknown" &&
          lang.isocode &&
          lang.isocode.trim() !== ""
      )
    : [];

  // Obtener el índice del idioma seleccionado
  const selectedLanguageIndex = validLanguages.findIndex(
    (lang) => lang.isocode === selectedLanguage
  );

  const handleLanguageChange = (_, newValue) => {
    const newLang = validLanguages[newValue]?.isocode || "es";
    setSelectedLanguage(newLang);
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/literals"),
            onSave: saveLiteral,
            showDelete: false,
          })}
          <CardHeader title={`Edit Literal: ${literal.nombre}`}></CardHeader>
          <CardBody>
            <Paper style={{ padding: "20px", marginBottom: "20px" }}>
              <Typography variant="h6" style={{ marginBottom: "16px" }}>
                Sección: {literal.nombre}
              </Typography>
              
              {/* Selector de idioma general */}
              {validLanguages.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" style={{ marginBottom: "8px", fontWeight: "bold" }}>
                    Idioma:
                  </Typography>
                  <AppBar position="static" color="default">
                    <Tabs
                      value={selectedLanguageIndex >= 0 ? selectedLanguageIndex : 0}
                      onChange={handleLanguageChange}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {validLanguages.map((lang) => (
                        <Tab key={lang.isocode} label={lang.fullName} />
                      ))}
                    </Tabs>
                  </AppBar>
                </Box>
              )}

              {/* Campos de literal en dos columnas */}
              <Grid container spacing={3}>
                {Object.keys(literal.content || {}).map((key) => {
                  const value = literal.content[key];
                  // Si es un objeto anidado (subsección), ocupa el ancho completo
                  const isNested = value && typeof value === "object" && !Array.isArray(value) && 
                    !(value.es !== undefined || value.en !== undefined || value.ca !== undefined);
                  
                  return (
                    <Grid item xs={12} sm={isNested ? 12 : 6} key={key}>
                      <LiteralField
                        path={key}
                        value={value}
                        onChange={handleFieldChange}
                        level={0}
                        selectedLanguage={selectedLanguage}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </CardBody>
        </Card>
      </>
    );
}
